/**
 * hi-shipping.js — cálculo de frete nativo (sem app).
 *
 * <hi-shipping-calc data-mode="cart">
 *   Cotação REAL pela API de carrinho da Shopify
 *   (/cart/prepare_shipping_rates.json + /cart/async_shipping_rates.json).
 *   Inclui tarifas de transportadora/apps configuradas em Frete e entrega.
 *
 * <hi-shipping-calc data-mode="product">
 *   A Shopify só cota frete para o que já está no carrinho. Na página de
 *   produto, o tema descobre o estado pelo CEP (faixas oficiais dos Correios,
 *   sem API externa) e aplica as regras de frete configuradas no tema ao kit
 *   escolhido + o que já estiver no carrinho. O valor exato sai no carrinho.
 */

import { StandardEvents } from '@shopify/events';

// Faixas de CEP por UF (5 primeiros dígitos). Fonte: Correios.
const CEP_RANGES = [
  [1000, 19999, 'SP'], [20000, 28999, 'RJ'], [29000, 29999, 'ES'], [30000, 39999, 'MG'],
  [40000, 48999, 'BA'], [49000, 49999, 'SE'], [50000, 56999, 'PE'], [57000, 57999, 'AL'],
  [58000, 58999, 'PB'], [59000, 59999, 'RN'], [60000, 63999, 'CE'], [64000, 64999, 'PI'],
  [65000, 65999, 'MA'], [66000, 68899, 'PA'], [68900, 68999, 'AP'], [69000, 69299, 'AM'],
  [69300, 69399, 'RR'], [69400, 69899, 'AM'], [69900, 69999, 'AC'], [70000, 72799, 'DF'],
  [72800, 72999, 'GO'], [73000, 73699, 'DF'], [73700, 76799, 'GO'], [76800, 76999, 'RO'],
  [77000, 77999, 'TO'], [78000, 78899, 'MT'], [79000, 79999, 'MS'], [80000, 87999, 'PR'],
  [88000, 89999, 'SC'], [90000, 99999, 'RS'],
];

const STORAGE_KEY = 'hi-cep';
const money = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const brl = (cents) => money.format(cents / 100);

export function ufFromCep(cep) {
  const digits = String(cep).replace(/\D/g, '');
  if (digits.length !== 8) return null;
  const prefix = Number(digits.slice(0, 5));
  const hit = CEP_RANGES.find(([min, max]) => prefix >= min && prefix <= max);
  return hit ? hit[2] : null;
}

const formatCep = (value) => {
  const d = value.replace(/\D/g, '').slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
};

const store = {
  get() {
    try {
      return localStorage.getItem(STORAGE_KEY) || '';
    } catch {
      return '';
    }
  },
  set(v) {
    try {
      localStorage.setItem(STORAGE_KEY, v);
    } catch {
      /* navegação privada: segue sem lembrar o CEP */
    }
  },
};

const escapeHtml = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

class HiShippingCalc extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[name="cep"]');
    this.result = this.querySelector('[data-result]');
    if (!this.form || !this.input || !this.result) return;

    this.input.value = formatCep(store.get());
    this.input.addEventListener('input', this.#onInput);
    this.form.addEventListener('submit', this.#onSubmit);

    if (this.dataset.mode === 'product') {
      // Recalcula quando o cliente troca de kit.
      document.addEventListener('change', this.#onKitChange);
    } else {
      // No carrinho, o resumo é re-renderizado a cada alteração: refaz a cotação.
      document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
      document.addEventListener(StandardEvents.cartDiscountUpdate, this.#onCartUpdate);
      if (this.input.value.length === 9) this.#calculate();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.#onKitChange);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#onCartUpdate);
  }

  #onCartUpdate = (event) => {
    const saved = formatCep(store.get());
    if (saved.length !== 9) return;
    Promise.resolve(event?.promise)
      .catch(() => {})
      .then(() => new Promise((r) => setTimeout(r, 350)))
      .then(() => {
        if (!this.isConnected) return;
        this.input.value = saved;
        this.#calculate();
      });
  };

  #onInput = () => {
    this.input.value = formatCep(this.input.value);
  };

  #onSubmit = (event) => {
    event.preventDefault();
    this.#calculate();
  };

  #onKitChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.closest('hi-kit-selector')) return;
    if (this.result.childElementCount) this.#calculate();
  };

  async #calculate() {
    const cep = this.input.value;
    const uf = ufFromCep(cep);
    if (!uf) {
      this.#render(`<p class="hi-ship__error">Confira o CEP: ele precisa ter 8 números.</p>`);
      this.input.focus();
      return;
    }
    store.set(cep);
    this.setAttribute('aria-busy', 'true');
    this.#render(`<p class="hi-ship__loading">Calculando…</p>`);
    try {
      if (this.dataset.mode === 'cart') await this.#cartRates(cep, uf);
      else await this.#productEstimate(cep, uf);
    } catch (error) {
      console.warn('[hi-shipping]', error);
      this.#render(`<p class="hi-ship__error">Não conseguimos calcular agora. O valor aparece no checkout.</p>`);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  // ---------- Página de produto: regras do tema ----------
  async #productEstimate(cep, uf) {
    const d = this.dataset;
    const regionalStates = (d.regionalStates || '').toUpperCase().split(/[\s,;]+/).filter(Boolean);
    const isRegional = regionalStates.includes(uf);
    const regionalFree = Number(d.regionalFree || 0);
    const nationalFree = Number(d.nationalFree || 0);
    const regionalFee = Number(d.regionalFee || 0);

    // Kit escolhido (já com a economia do kit) ou preço × quantidade.
    const scope = this.closest('.shopify-section') || document;
    const kit = scope.querySelector('hi-kit-selector input[type="radio"]:checked');
    let items = kit ? Number(kit.dataset.total || 0) : Number(d.price || 0);
    if (!kit) {
      const qty = scope.querySelector('product-form-component input[name="quantity"]');
      items *= Math.max(1, Number(qty?.value || 1));
    }

    let inCart = 0;
    try {
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
      inCart = Number(cart.items_subtotal_price || 0);
    } catch {
      /* sem carrinho: considera só o produto */
    }
    const total = items + inCart;
    const cartNote = inCart > 0 ? ' (somando o que já está no seu carrinho)' : '';

    const rows = [];
    if (kit?.dataset.freeShipping === 'true') {
      rows.push(this.#row('Entrega econômica', 'Grátis neste kit', true));
    } else if (isRegional && regionalFree && total >= regionalFree) {
      rows.push(this.#row('Entrega econômica', 'Grátis', true));
    } else if (nationalFree && total >= nationalFree) {
      rows.push(this.#row('Entrega econômica', 'Grátis', true));
    } else if (isRegional && regionalFee) {
      rows.push(this.#row('Entrega econômica', brl(regionalFee)));
      rows.push(`<p class="hi-ship__hint">Faltam <strong>${brl(regionalFree - total)}</strong> para frete grátis${cartNote}.</p>`);
    } else {
      rows.push(this.#row('Entrega econômica', 'Calculada no carrinho'));
      if (nationalFree) {
        rows.push(`<p class="hi-ship__hint">Grátis a partir de ${brl(nationalFree)} — faltam <strong>${brl(nationalFree - total)}</strong>${cartNote}.</p>`);
      }
    }

    this.#render(`
      <p class="hi-ship__where">CEP ${escapeHtml(cep)} · ${uf}</p>
      <ul class="hi-ship__list">${rows.filter((r) => r.startsWith('<li')).join('')}</ul>
      ${rows.filter((r) => !r.startsWith('<li')).join('')}
      ${inCart > 0 ? `<p class="hi-ship__note">Considerando o kit escolhido + ${brl(inCart)} que já estão no seu carrinho.</p>` : ''}
      <p class="hi-ship__note">Prazo e modalidades expressas aparecem no carrinho e no checkout.</p>
    `);
  }

  // ---------- Carrinho: cotação real da Shopify ----------
  async #cartRates(cep, uf) {
    const root = window.Shopify?.routes?.root || '/';
    const params = new URLSearchParams({
      'shipping_address[zip]': cep,
      'shipping_address[country]': 'Brazil',
      'shipping_address[province]': uf,
    }).toString();

    const prepare = await fetch(`${root}cart/prepare_shipping_rates.json?${params}`, { method: 'POST' });
    if (prepare.status === 422) return this.#renderErrors(await prepare.json());

    let rates = null;
    for (let i = 0; i < 12 && rates === null; i++) {
      const res = await fetch(`${root}cart/async_shipping_rates.json?${params}`);
      if (res.status === 422) return this.#renderErrors(await res.json());
      const data = await res.json();
      if (Array.isArray(data?.shipping_rates)) rates = data.shipping_rates;
      else await new Promise((r) => setTimeout(r, 500));
    }
    if (rates === null) {
      const res = await fetch(`${root}cart/shipping_rates.json?${params}`);
      rates = (await res.json())?.shipping_rates || [];
    }

    if (!rates.length) {
      this.#render(`<p class="hi-ship__error">Nenhuma modalidade de entrega para o CEP ${escapeHtml(cep)}.</p>`);
      return;
    }
    const items = rates
      .map((rate) => {
        const cents = Math.round(Number(rate.price) * 100);
        const days = Array.isArray(rate.delivery_days) && rate.delivery_days.length ? rate.delivery_days : null;
        const eta = days ? (days[0] === days[1] || days.length === 1 ? `${days[0]} dias úteis` : `${days[0]} a ${days[1]} dias úteis`) : '';
        return this.#row(rate.presentment_name || rate.name, cents === 0 ? 'Grátis' : brl(cents), cents === 0, eta);
      })
      .join('');
    this.#render(`<p class="hi-ship__where">CEP ${escapeHtml(cep)} · ${uf}</p><ul class="hi-ship__list">${items}</ul>`);
  }

  #renderErrors(data) {
    const msg = Object.values(data || {}).flat().join(' ') || 'CEP inválido.';
    this.#render(`<p class="hi-ship__error">${escapeHtml(msg)}</p>`);
  }

  #row(name, value, free = false, eta = '') {
    return `<li class="hi-ship__rate"><span><strong>${escapeHtml(name)}</strong>${eta ? `<small>${escapeHtml(eta)}</small>` : ''}</span><span class="hi-ship__price${free ? ' hi-ship__price--free' : ''}">${escapeHtml(value)}</span></li>`;
  }

  #render(html) {
    this.result.innerHTML = html;
  }
}

if (!customElements.get('hi-shipping-calc')) customElements.define('hi-shipping-calc', HiShippingCalc);
